import { useMemo, useState } from 'react';
import { CheckCircle2, RotateCcw, SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../ui';
import type { AcousticCalibrationSnapshot, AcousticCalibrationTag } from '../../lib/acoustic/calibrationTypes';
import { ACOUSTIC_CALIBRATION_TAGS } from '../../lib/acoustic/calibrationTypes';
import { ACOUSTIC_FACTORY_DEFAULTS } from '../../lib/acoustic/config';
import { getAcousticConfig } from '../../lib/acoustic/acousticRuntimeConfig';
import {
  applyAllEmpiricalThresholdsFromSnapshots,
  applyThresholdOverridesForTag,
  clearThresholdOverridesForTag
} from '../../lib/acoustic/acousticThresholdOverrides';
import {
  deriveSilenceBaselineOverrides,
  deriveThresholdOverridesForTag
} from '../../lib/acoustic/calibrationThresholdDeriver';
import { useAcousticThresholdOverrides } from '../../hooks/useAcousticThresholdOverrides';
import CategoryContinuumTable from './CategoryContinuumTable';
import {
  CALIBRABLE_SITUATION_TAGS,
  getCalibrationReadiness,
  MIN_SNAPSHOTS_PER_CATEGORY
} from '../../lib/acoustic/calibrationReadiness';

type AcousticThresholdPanelProps = {
  snapshots: AcousticCalibrationSnapshot[];
  filterTag: AcousticCalibrationTag | 'all';
};

export default function AcousticThresholdPanel({ snapshots, filterTag }: AcousticThresholdPanelProps) {
  const { store, empiricalGlobalActive, activeOverrides, resetAll, refresh } = useAcousticThresholdOverrides();
  const [lastSummary, setLastSummary] = useState<string[]>([]);
  const effectiveConfig = getAcousticConfig();

  const readiness = useMemo(() => getCalibrationReadiness(snapshots), [snapshots]);

  const targetTag: AcousticCalibrationTag | null =
    filterTag !== 'all' && CALIBRABLE_SITUATION_TAGS.includes(filterTag) ? filterTag : 'babble';

  const tagLabel = ACOUSTIC_CALIBRATION_TAGS.find((t) => t.key === targetTag)?.label ?? 'Gemurmel';
  const tagCount = targetTag ? snapshots.filter((s) => s.tag === targetTag).length : 0;
  const tagReady = tagCount >= MIN_SNAPSHOTS_PER_CATEGORY;

  const tagOverrides = targetTag ? store.byTag[targetTag] : undefined;

  const preview = useMemo(() => {
    if (!targetTag) return null;
    return deriveThresholdOverridesForTag(targetTag, snapshots);
  }, [targetTag, snapshots]);

  const handleApply = () => {
    if (!targetTag) return;
    const result = deriveThresholdOverridesForTag(targetTag, snapshots);
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    applyThresholdOverridesForTag(targetTag, result.overrides);
    refresh();
    setLastSummary(result.summary);
    toast.success(`Schwellen für „${result.tagLabel}" global aktiv (${result.snapshotCount} Snapshots)`);
  };

  const handleApplySilenceBaseline = () => {
    const result = deriveSilenceBaselineOverrides(snapshots);
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    applyThresholdOverridesForTag('silence', result.overrides);
    refresh();
    setLastSummary(result.summary);
    toast.success(`Rauschboden aus ${result.snapshotCount} Stille-Snapshots übernommen`);
  };

  const handleApplyAll = () => {
    const result = applyAllEmpiricalThresholdsFromSnapshots(snapshots);
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    refresh();
    setLastSummary([
      `Global aktiv: ${result.appliedTags.map((t) => ACOUSTIC_CALIBRATION_TAGS.find((m) => m.key === t)?.label ?? t).join(', ')}`,
      ...result.skippedTags.map((s) => `Übersprungen ${s.tag}: ${s.reason}`)
    ]);
    toast.success(
      `Empirische Schwellen global aktiv (${result.appliedTags.length} Kategorien) – gilt im Live-Unterricht`
    );
  };

  const handleResetTag = () => {
    if (!targetTag) return;
    clearThresholdOverridesForTag(targetTag);
    refresh();
    setLastSummary([]);
    toast.success(`Schwellen für „${tagLabel}" zurückgesetzt`);
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
      {empiricalGlobalActive ? (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Empirische Schwellen sind global aktiv.</strong> Gespeichert in der Datenbank – Live-Unterricht
            und Kalibrierung auf allen Geräten nutzen deine Werte statt der Werkseinstellungen im Code.
          </span>
        </div>
      ) : (
        <p className="text-xs text-amber-800 dark:text-amber-200">
          Aktuell gelten noch Werkseinstellungen. Mit „Global aktivieren“ ersetzt du sie app-weit durch deine
          Snapshots.
        </p>
      )}

      {!readiness.canActivateGlobally && readiness.blockReason ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {readiness.blockReason}
        </p>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <SlidersHorizontal className="h-4 w-4" />
            Schwellenwerte aus Snapshots
          </h3>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Kontinuum (min · Schwerpunkt · max) pro Kategorie → Schwellen landen in der Datenbank und gelten
            schulweit im Live-Unterricht.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="primary"
          onClick={handleApplyAll}
          disabled={!readiness.canActivateGlobally}
          title={
            readiness.canActivateGlobally
              ? `Bereit: ${readiness.readyTags.map((t) => ACOUSTIC_CALIBRATION_TAGS.find((m) => m.key === t)?.label ?? t).join(', ')}`
              : readiness.blockReason ?? undefined
          }
        >
          Alle Kategorien global aktivieren
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleApply}
          disabled={!tagReady}
          title={
            tagReady
              ? undefined
              : `Mindestens ${MIN_SNAPSHOTS_PER_CATEGORY} Snapshots mit Tag „${tagLabel}" nötig (aktuell: ${tagCount}).`
          }
        >
          Nur {tagLabel} ({tagCount})
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleApplySilenceBaseline}
          disabled={!readiness.canApplySilenceBaseline}
          title={
            readiness.canApplySilenceBaseline
              ? 'Setzt Mindest-Schwellen über dem erfassten Klassenrausch (Fusion Rauschen / Lautstärke)'
              : `Mindestens ${MIN_SNAPSHOTS_PER_CATEGORY} Stille-Snapshots nötig`
          }
        >
          Rauschboden aus Stille ({readiness.silenceSnapshotCount})
        </Button>
        <Button size="sm" variant="outline" onClick={handleResetTag} disabled={!tagOverrides}>
          Nur {tagLabel} zurücksetzen
        </Button>
        <Button size="sm" variant="outline" onClick={resetAll} disabled={!empiricalGlobalActive}>
          <RotateCcw className="mr-1 h-3.5 w-3.5" />
          Werkseinstellungen
        </Button>
      </div>

      <p className="text-xs text-gray-700 dark:text-gray-300">
        {readiness.calibrableSnapshotCount} kalibrierbare Snapshots (Gemurmel, Geschrei, Einzelsprecher, Dialog)
        {readiness.silenceSnapshotCount > 0
          ? ` · ${readiness.silenceSnapshotCount}× Stille (Rauschboden${readiness.canApplySilenceBaseline ? ', übernehmbar' : ''})`
          : ''}
        {readiness.readyTags.length > 0
          ? ` · bereit: ${readiness.readyTags
              .map((t) => ACOUSTIC_CALIBRATION_TAGS.find((m) => m.key === t)?.label ?? t)
              .join(', ')}`
          : ''}
        {filterTag !== 'all' ? ` · Filter: ${tagLabel} (${tagCount})` : ''}
      </p>

      <CategoryContinuumTable snapshots={snapshots} highlightTag={targetTag ?? undefined} />

      {preview?.ok && tagReady ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-gray-600 dark:text-gray-400">
            Vorschau nur {tagLabel}
          </summary>
          <ul className="mt-2 space-y-0.5 font-mono text-gray-700 dark:text-gray-300">
            {preview.summary.map((line) => (
              <li key={line} className={line.startsWith('  ') ? 'pl-2' : ''}>
                {line}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {lastSummary.length ? (
        <div className="text-xs">
          <p className="font-medium text-gray-700 dark:text-gray-300">Zuletzt übernommen:</p>
          <ul className="mt-1 space-y-0.5 font-mono text-gray-600 dark:text-gray-400">
            {lastSummary.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <details className="text-xs" open={empiricalGlobalActive}>
        <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-300">
          Aktuell gültige Situations-Schwellen (global)
        </summary>
        <ul className="mt-2 space-y-1 font-mono text-gray-600 dark:text-gray-400">
          <li>
            Gemurmel Lautst. min/max: {effectiveConfig.babbleLoudnessMin} / {effectiveConfig.babbleLoudnessMax}{' '}
            <span className="text-gray-400">
              (Werk: {ACOUSTIC_FACTORY_DEFAULTS.babbleLoudnessMin} / {ACOUSTIC_FACTORY_DEFAULTS.babbleLoudnessMax})
            </span>
          </li>
          <li>
            Gemurmel Diffusheit min: {effectiveConfig.babbleDiffusenessMin}{' '}
            <span className="text-gray-400">(Werk: {ACOUSTIC_FACTORY_DEFAULTS.babbleDiffusenessMin})</span>
          </li>
          <li>
            Geschrei Lautst. min: {effectiveConfig.shoutingLoudnessThreshold}{' '}
            <span className="text-gray-400">(Werk: {ACOUSTIC_FACTORY_DEFAULTS.shoutingLoudnessThreshold})</span>
          </li>
          <li>YAMNet Gemurmel min: {effectiveConfig.yamnetBabbleTriggerMin}</li>
          <li>YAMNet Geschrei min: {effectiveConfig.yamnetShoutingTriggerMin}</li>
        </ul>
        {activeOverrides.length ? (
          <p className="mt-2 text-gray-500">{activeOverrides.length} Werte weichen von der Werkseinstellung ab.</p>
        ) : null}
      </details>
    </div>
  );
}